import { useState, useEffect, useRef, useMemo } from "preact/hooks";
import { useReviews } from "@/hooks/useReviews";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { VerifiedFooter } from "./VerifiedFooter";
import { ReviewsSheet } from "./ReviewsSheet";
import { ReviewItem } from "./ReviewItem";
import { trackEvent, type ModalOpenSource } from "@/utils/tracking";
import { publishRatingsTotal } from "@/utils/ratingsBadge";
import { widgetEventBus } from "@/lib/widgetEvents";
import { FilterChips } from "./ui/filter-chips";
import { getProductTabs } from "@/utils/metadata";
import StarRating from "./StarRating";

// Inline preview count. The rest live behind "Show all" in the sheet.
const PREVIEW_COUNT = 3;

// Temporarily hidden: the "Featured reviews, independently verified by Social
// Ocean" line under the carousel. Flip back to true to restore it.
const SHOW_VERIFIED_FOOTER = false;

export function ReviewCarousel() {
  // Product tabs replace the topic chips when the page lists products to
  // review (a ritual, which has no reviews of its own). Read once: the theme
  // renders the list server-side and it does not change under us.
  const productTabs = useMemo(() => getProductTabs(), []);
  const hasProductTabs = productTabs.length > 0;
  const [tabProductId, setTabProductId] = useState<number | null>(
    productTabs[0]?.id ?? null,
  );
  const activeTab = productTabs.find((t) => t.id === tabProductId) ?? null;

  const { data, isLoading, isPlaceholderData } = useReviews(
    null,
    hasProductTabs ? tabProductId : null,
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [sheetTopic, setSheetTopic] = useState<string | null>(null);
  const [openSource, setOpenSource] =
    useState<ModalOpenSource>("other_external");

  const openSheet = (source: ModalOpenSource, topic: string | null = null) => {
    setOpenSource(source);
    setSheetTopic(topic);
    setIsSheetOpen(true);
  };

  const productId = data?.product_id;

  // Fire reviews.widget_viewed ONCE, when the carousel actually scrolls into
  // view — not on data-load (which would count users who never saw it).
  // threshold 0 (any part visible) so a tall carousel on a short viewport can't
  // fail to reach the ratio; rootMargin 0 so it doesn't pre-fire off-screen.
  const hasReviews = !!data?.reviews.length;
  const viewedFiredRef = useRef(false);
  const widgetRef = useIntersectionObserver({
    enabled: hasReviews && !!productId,
    threshold: 0,
    rootMargin: "0px",
    onIntersect: () => {
      if (viewedFiredRef.current) return;
      viewedFiredRef.current = true;
      trackEvent("reviews.widget_viewed");
    },
  });

  useEffect(
    () =>
      widgetEventBus.subscribe((event) => {
        if (event.type === "open") openSheet(event.source, event.topic ?? null);
      }),
    [],
  );

  // Hand the real ratings count to the theme's star badge under the product
  // title. The theme's count-up loader animates the placeholder and settles on
  // this number; we only fall back to writing it ourselves where no loader is
  // present. See utils/ratingsBadge.
  //
  // Not with product tabs: the badge describes the page's product, and one
  // tab's total would mislabel it as that product's count. The theme sums the
  // tabbed products itself.
  const ratingsTotal = data?.total;
  useEffect(() => {
    if (hasProductTabs) return;
    if (ratingsTotal == null || ratingsTotal <= 0) return;
    publishRatingsTotal(ratingsTotal);
  }, [ratingsTotal, hasProductTabs]);

  if (isLoading) {
    return <div></div>;
  }

  // With tabs, an empty product must not take the tabs down with it -- the
  // shopper could never switch to one that has reviews.
  if (!hasProductTabs && (!data || data?.reviews.length === 0)) {
    return <div></div>;
  }

  if (!data) {
    return <div></div>;
  }

  const { reviews, total } = data;
  const preview = reviews.slice(0, PREVIEW_COUNT);

  return (
    <>
      <div ref={widgetRef} className="tw:mx-auto tw:max-w-89.5 tw:md:max-w-300">
        {/* Header mirrors the sheet header: title, stars + ratings, chips. */}
        <div className="tw:flex tw:flex-col tw:items-center tw:gap-1.5 tw:text-center">
          <h2 className="tw:text-2xl tw:font-medium tw:leading-none tw:tracking-tight tw:text-[#523C33]">
            From Loyal Customers
          </h2>
          <div className="tw:flex tw:items-center tw:justify-start tw:gap-1.5">
            <StarRating rating={5} size="sm" />
            <span className="tw:text-sm tw:font-medium">
              {total.toLocaleString()}{" "}
            </span>
            <span className="tw:text-sm tw:font-medium tw:text-muted-foreground">
              ratings
            </span>
          </div>
          <div className="tw:w-full">
            {hasProductTabs ? (
              <FilterChips
                ariaLabel="Show reviews for"
                centered
                filters={productTabs.map((t) => ({
                  key: String(t.id),
                  label: t.label,
                  emoji: "",
                  count: 0,
                }))}
                active={tabProductId != null ? String(tabProductId) : null}
                onSelect={(key) => {
                  // Tapping the active tab would clear it; a tab row always
                  // has one selected, so that tap does nothing.
                  if (!key) return;
                  trackEvent("reviews.filter_chip_tapped", {
                    chip: `product:${key}`,
                    surface: "carousel",
                  });
                  setTabProductId(Number(key));
                  setSheetTopic(null);
                }}
              />
            ) : (
              <FilterChips
                filters={data.filters}
                active={null}
                onSelect={(key) => {
                  if (!key) return;
                  trackEvent("reviews.filter_chip_tapped", {
                    chip: key,
                    surface: "carousel",
                  });
                  openSheet("chip", key);
                }}
              />
            )}
          </div>
        </div>

        <div
          className={
            isPlaceholderData ? "tw:opacity-60 tw:transition-opacity" : undefined
          }
        >
          {preview.map((review, index) => (
            <ReviewItem
              key={`${tabProductId ?? "page"}-${index}`}
              review={review}
              surface="carousel"
              index={index}
              last={index === preview.length - 1}
            />
          ))}
        </div>

        <div className="tw:mt-4 tw:flex tw:justify-center">
          <button
            type="button"
            onClick={() => openSheet("show_all_button")}
            className="tw:bg-primary tw:py-2.5 tw:w-full tw:text-base tw:font-bold tw:text-white tw:cursor-pointer hover:tw:bg-[#f8d4d6] tw:transition-colors tw:border-0"
          >
            Show All
          </button>
        </div>

        {SHOW_VERIFIED_FOOTER ? (
          <div className="tw:mt-4">
            <VerifiedFooter />
          </div>
        ) : null}
      </div>

      <ReviewsSheet
        isOpen={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        source={openSource}
        topic={sheetTopic}
        onTopicChange={setSheetTopic}
        fallbackFilters={data.filters}
        productId={hasProductTabs ? tabProductId : null}
        productLabel={activeTab?.label ?? null}
        productTabs={productTabs}
        onProductChange={(id) => {
          setTabProductId(id);
          setSheetTopic(null);
        }}
      />
    </>
  );
}
